import React from 'react';

import Router, { useRouter } from 'next/router';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';

import useAnalytics from './useAnalytics';

type AnalyticsProps = {
  children: React.ReactNode;
};
/**
 * Wrapper to fire segment analytics calls on each page change.
 */
const AnalyticsWrapper: React.FC<AnalyticsProps> = ({ children }) => {
  const { page, identify, load, LOAD_DELAY } = useAnalytics();
  const { user } = useNonNullContext(UserContext);
  const router = useRouter();

  const pageCall = () => {
    const pageName = router.asPath;
    page(pageName);
  };

  const [shouldFirePageCall, setShouldFirePageCall] = React.useState(false);
  React.useEffect(() => {
    // capture initial component mount -- this will be fired if user navigates to a page served by this repo
    // directly, or if another FE repo routes here
    load();
    setTimeout(() => {
      pageCall();
    }, LOAD_DELAY);
    // when route change starts update flag indicating we should log when router is ready.
    Router.events.on('routeChangeStart', () => {
      setShouldFirePageCall(true);
    });
  }, []);

  /**
   * The router can be stale for first few renders; the `router.isReady` field indicates that the router properties
   * are updated and ready for use.
   *
   * Combination of this hook and the `shouldFirePageCall` lets us fire one non-stale call per page route.
   */
  React.useEffect(() => {
    // short circuit if not ready.
    if (!router.isReady) {
      return;
    }
    if (shouldFirePageCall) {
      //this covers after a user logs in or signs up, as they immediately get redirected.
      if (user?.id) {
        identify();
      }
      pageCall();
      setShouldFirePageCall(false);
    }
  }, [router, user?.id]);

  return <>{children}</>;
};

export default AnalyticsWrapper;
